<!-- ItemsList.svelte -->
<script>
  import { onMount } from 'svelte';
  import { Link, router } from '@inertiajs/svelte'
  import ItemRow from './ItemRow/index.svelte'
  import DeleteConfirmation from './DeleteConfirmation.svelte'
  import MoveItemModal from './MoveItemModal/index.svelte'

  let {
    items = [],
    propertyId,
    paginationMeta = {},
    // The infinite-scroll endpoint. Defaults to the property's items; the
    // container page passes its own /containers/:id/items endpoint so the same
    // component drives both lists.
    loadUrl = null,
    // Where "+ Add item" points (e.g. prefilled with a container_id).
    addItemHref = null
  } = $props()

  const itemsLoadUrl = $derived(loadUrl || `/properties/${propertyId}/items`)
  const newItemHref = $derived(addItemHref || `/properties/${propertyId}/items/new`)

  // State for delete confirmation
  let showDeleteConfirm = $state(false)
  let itemToDelete = $state(null)

  // State for the move modal
  let itemToMove = $state(null)

  // Pagination state
  let allItems = $state([...items])
  let isLoading = $state(false)
  let currentPage = $state(1)
  let hasMorePages = $state(paginationMeta.has_more_pages || false)
  let totalCount = $state(paginationMeta.total_count || items.length)
  let scrollContainer = $state()

  // Load more items
  async function loadMoreItems() {
    if (isLoading || !hasMorePages) return;

    isLoading = true;

    try {
      const sep = itemsLoadUrl.includes('?') ? '&' : '?'
      const response = await fetch(
        `${itemsLoadUrl}${sep}page=${currentPage + 1}`,
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load items');
      }

      const data = await response.json();

      allItems = [...allItems, ...data.items];
      currentPage = data.pagy.page;
      hasMorePages = data.pagy.page < data.pagy.pages;

      // Update total count if provided
      if (data.pagy.total_count !== undefined) {
        totalCount = data.pagy.total_count;
      }

    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      isLoading = false;
    }
  }

  // Infinite scroll handler
  function handleScroll() {
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const threshold = 50; // Load more when 50px from bottom

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadMoreItems();
    }
  }

  function handleDeleteClick(item) {
    itemToDelete = item
    showDeleteConfirm = true
  }

  function handleDeleteConfirm() {
    if (itemToDelete) {
      router.delete(`/properties/${propertyId}/items/${itemToDelete.id}`)
    }
  }

  function handleDeleteCancel() {
    itemToDelete = null
    showDeleteConfirm = false
  }

  // Check if we should load more on mount
  onMount(() => {
    // If we have less than 10 items total, there might be more to load
    if (allItems.length < 10 && hasMorePages) {
      loadMoreItems();
    }
  });
</script>

<section class="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
  <div class="flex justify-between items-center gap-3 px-5 py-4 border-b border-gray-100">
    <h2 class="text-base font-bold text-gray-900 flex items-center gap-2">
      Items
      {#if totalCount > 0}
        <span class="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{totalCount}</span>
      {/if}
    </h2>
    <Link href={newItemHref} class="text-sm font-semibold text-brand-700 hover:underline">
      + Add item
    </Link>
  </div>

  {#if allItems.length === 0 && !isLoading}
    <div class="text-center py-10 px-5">
      <div class="text-gray-400 mb-4">
        <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      </div>
      <p class="text-gray-500">No items added yet.</p>
      <Link href={newItemHref} class="mt-4 inline-block text-sm font-semibold text-brand-700 hover:underline">
        + Add your first item
      </Link>
    </div>
  {:else}
    <!-- Scrollable container for infinite scroll -->
    <div
      bind:this={scrollContainer}
      onscroll={handleScroll}
      class="max-h-96 overflow-y-auto px-2 py-1.5"
    >
      <ul class="divide-y divide-gray-100">
        {#each allItems as item (item.id)}
          <ItemRow {item} onMove={(it) => (itemToMove = it)} onDelete={handleDeleteClick} />
        {/each}
      </ul>

      <!-- Loading indicator -->
      {#if isLoading}
        <div class="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
          <span class="h-3.5 w-3.5 rounded-full border-2 border-gray-200 border-t-brand-600 animate-spin"></span>
          Loading more items…
        </div>
      {/if}

      <!-- End of results message -->
      {#if !hasMorePages && allItems.length > 0 && !isLoading}
        <div class="text-center py-3 text-xs text-gray-400">
          All {allItems.length} items loaded
        </div>
      {/if}
    </div>

    {#if hasMorePages && !isLoading}
      <div class="text-center py-2.5 border-t border-gray-100 text-xs text-gray-400">
        Showing {allItems.length} of {totalCount} · more load as you scroll
      </div>
    {/if}
  {/if}
</section>

<DeleteConfirmation
  show={showDeleteConfirm}
  title="Delete Item"
  message="Are you sure you want to delete this item? This action cannot be undone and will remove any associated images."
  onConfirm={handleDeleteConfirm}
  onCancel={handleDeleteCancel}
/>

<MoveItemModal open={!!itemToMove} item={itemToMove} onClose={() => (itemToMove = null)} />
